import { useCallback, useEffect, useState } from "react";
import { useCoverUrl } from "./useCoverUrl";
import { PLACEHOLDER_IMAGE } from "../lib/events";

// Single source of truth for rendering an event cover anywhere (cards, drawer,
// list, search, calendar, saved, featured, event page). Handles two independent
// recovery paths:
//   1. Supabase signed covers (event.coverPath) — lazily signed & re-signed on
//      error via useCoverUrl (existing behaviour, unchanged).
//   2. External images — `event.image` is the weserv-proxied URL (resized/cheap).
//      If the proxy is rejected by the origin, we automatically retry the RAW
//      `event.imageRaw` (original external_image_url), then fall back to the
//      generic placeholder as a last resort.
export function useEventCover(event, hero = false) {
  const coverPath = event?.coverPath || null;
  const { url: coverUrl, onError: onCoverError } = useCoverUrl(coverPath, hero);

  const primary = event?.image || null; // proxied external OR signed hero (from fetchEvent)
  const raw = event?.imageRaw || null; // raw external_image_url

  // stage: 0 = proxied/primary, 1 = raw external, 2 = placeholder
  const [stage, setStage] = useState(0);
  useEffect(() => {
    setStage(0);
  }, [primary, raw]);

  const externalSrc =
    stage === 0
      ? primary || raw || PLACEHOLDER_IMAGE
      : stage === 1
      ? raw || PLACEHOLDER_IMAGE
      : PLACEHOLDER_IMAGE;

  const src = coverUrl || externalSrc;

  const onError = useCallback(() => {
    if (coverUrl) {
      onCoverError(); // signed Supabase cover: re-sign once
      return;
    }
    setStage((s) => (s < 2 ? s + 1 : s)); // external chain: proxied -> raw -> placeholder
  }, [coverUrl, onCoverError]);

  return { src, onError };
}
